/** @jsxImportSource @emotion/react */
import * as authSelectors from "../../auth/authSelectors";
import { TextInput } from "../../../components/forms/inputs/TextInput";
import { useSelector } from "react-redux";
import { useGetKitchenCategories } from "../../kitchen-categories/service/kitchen-categories-api";
import { SelectInput } from "../../../components/forms/inputs/SelectInput";
import { SwitchInput } from "../../../components/forms/inputs/SwitchInput";
import { SpinnerBlock } from "../../common/Spinner";
import { useHandleFetchError } from "../../../error-handler";
import { Product, ProductClassification } from "../models/product";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { RoundedActionLabel } from "../../../components/RoundedActionLabel";
import { useFieldArray, useFormContext } from "react-hook-form";
import { css } from "@emotion/react";
import { effects } from "../../../components/effects";
import { colors } from "../../../components/colors";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { ProductUpdateFormValues } from "./UpdateProductDrawer";
import { StyledText } from "../../../assets/typography/typography";
import { BarcodeSelector } from "./BarcodeSelector";

type Props = {
  product: Product;
};

const barcodesContainerStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const additionalLabelStyle = css`
  box-shadow: ${effects.buttonPrimary};
  color: ${colors.textPrimary};
`;

export const AdvancedSettingsForm = ({ product }: Props) => {
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const methods = useFormContext<ProductUpdateFormValues>();
  const {
    data: kitchenCategories,
    isLoading: kitchenCategoriesAreLoading,
    error: listKitchenCategoriesError,
  } = useGetKitchenCategories(organizationKeyName);

  const { fields, append, remove } = useFieldArray({
    name: "barcodes",
    control: methods.control,
  });

  const kitchenCategoriesOptions = (kitchenCategories || [])
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((cat) => ({
      label: cat.name,
      value: cat.id,
    }));

  kitchenCategoriesOptions.unshift({
    label: "None",
    value: "",
  });

  useHandleFetchError(listKitchenCategoriesError);

  if (kitchenCategoriesAreLoading) {
    return <SpinnerBlock />;
  }

  return (
    <>
      <TextInput
        label="Product units"
        name="kitchen_capacity_units"
        type="number"
        placeholder="Enter product units"
        helperText="How many units should this product account for in capacity planning?"
      />
      <TextInput
        label="Sort key"
        name="sortKey"
        type="number"
        placeholder="Enter sort key"
        helperText="Enter a number to decide the sorting of the product within the product list. We recomend using intervals of 10. Eg. the first product should have sort key “10”. The second sort key “20” etc as all products are sorted ascending."
      />
      <SelectInput
        label="Kitchen category id"
        name="kitchenCategoryId"
        options={kitchenCategoriesOptions}
        helperText="The product will be shown in the chosen category on kitchen receipt."
      />
      <TextInput
        label="Alcohol percentage"
        name="alcoholPercentage"
        type="number"
        placeholder="Enter alcohol percentage"
        helperText="Type in the alcohol percentage of the product. Do not include %. e.g. for 12% ABV, please write 12"
      />
      <TextInput
        label="OnlinePos Product id"
        name="onlinePosProductId"
        placeholder="Enter OnlinePos product id"
        helperText="Product id from OnlinePos used for integration"
      />
      <TextInput
        label="Deliverect PLU"
        name="deliverectProductPlu"
        placeholder="Enter Deliverect PLU"
        helperText="PLU from Deliverect used for integration"
      />
      <TextInput
        label="Reference"
        name="reference"
        placeholder="Enter reference"
        helperText="External reference used for reporting. This could be a SKU or POS id."
      />

      <TextInput
        label="Weight"
        name="weight"
        placeholder="Enter weight"
        helperText="Weight of the product in grams"
      />
      <TextInput
        label="Length"
        name="dimensions.length"
        placeholder="Enter length"
        helperText="Length of the product in millimeters"
      />

      <TextInput
        label="Width"
        name="dimensions.width"
        placeholder="Enter width"
        helperText="Width of the product in millimeters"
      />
      <TextInput
        label="Height"
        name="dimensions.height"
        placeholder="Enter height"
        helperText="Height of the product in millimeters"
      />
      <TextInput
        label="Online POS serving group id"
        name="servingGroupId"
        placeholder="Enter Online POS serving group id"
        helperText="Serving group id from Online POS"
      />
      <TextInput
        label="Simphony POS product id"
        name="simphonyReference"
        placeholder="Enter Simphony POS product id"
        helperText="The product id from Simphony POS"
      />
      <div css={barcodesContainerStyle}>
        <StyledText
          className="bodySubtextSemibold mb-0"
          color={colors.textPrimary}
        >
          Barcodes
        </StyledText>
        {fields.length > 0 && (
          <div
            css={css`
              display: flex;
              flex-wrap: wrap;
              flex-direction: row;
              gap: 8px;
              margin-bottom: 16px;
            `}
          >
            {fields.map((field, index) => (
              <div
                key={field.id}
                css={css`
                  display: inline-block;
                `}
              >
                <RoundedActionLabel
                  key={field.id}
                  buttonIcon={<FontAwesomeIcon icon={solid("xmark")} />}
                  additionalStyles={additionalLabelStyle}
                  icon={<FontAwesomeIcon icon={solid("scanner")} />}
                  label={field.value}
                  onButtonClick={() => {
                    remove(index);
                  }}
                />
              </div>
            ))}
          </div>
        )}
        <BarcodeSelector
          onSubmit={(barcode) => {
            append(barcode);
          }}
        />
      </div>
      <SwitchInput
        label="Is eligible for discount. If disabled, product will not be eligible for discount from vouchers, discount cards e.g."
        name="isEligibleForDiscount"
      />
      <SwitchInput
        label="If enabled, the product is marked as a combo."
        name="isComboMeal"
      />
      <SwitchInput
        label="If enabled, the order in which the product is added to, will be required to be accepted manually in the restaurant by staff."
        name="requiresManualAccept"
      />
      {product.classification !== ProductClassification.Addition && (
        <SwitchInput
          label="Customer earns points for Challenges and Rewards when purchasing this product."
          name="allowChallengeProgress"
        />
      )}
    </>
  );
};
